import { ICrosshairConfig } from "../interfaces/interface";

export interface IRelatedCrosshair {
    id: number;
    name: string;
    slug: string;
}

export interface IPagedResult {
    items: ICrosshairConfig[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

function toConfig(row: Record<string, unknown>): ICrosshairConfig {
    return {
        id: row.id as number,
        name: row.name as string,
        slug: (row.slug as string) ?? String(row.id),
        code: row.code as string,
        isPro: Boolean(row.isPro),
        isFun: Boolean(row.isFun),
        favoriteCount: row.favoriteCount as number,
        isFavorited: Boolean(row.isFavorited),
        submissionStatus: (row.submissionStatus as string) ?? null,
    };
}

/** A single crosshair from the detail endpoints, which also say how its page is indexed. */
function toDetailConfig(row: Record<string, unknown>): ICrosshairConfig {
    return {
        ...toConfig(row),
        // Absent means an API older than this field: stay indexable rather than noindex everything.
        indexable: row.indexable !== false,
        canonicalSlug: (row.canonicalSlug as string) ?? null,
        sameCode: ((row.sameCode as Record<string, unknown>[]) ?? []).map((r) => ({
            id: r.id as number,
            name: r.name as string,
            slug: r.slug as string,
        })),
    };
}

async function get(endpoint: string, search?: string, page = 1, pageSize?: number): Promise<IPagedResult> {
    const url = new URL(`/api/crosshairs${endpoint}`, window.location.origin);
    if (search) url.searchParams.set("search", search);
    url.searchParams.set("page", String(page));
    if (pageSize != null && pageSize > 0) url.searchParams.set("pageSize", String(pageSize));

    const response = await fetch(url.toString(), { credentials: "include" });
    if (!response.ok) throw new Error(`Failed to fetch crosshairs: ${response.status}`);
    const data = await response.json();
    const rawItems = (data.items ?? data.Items ?? []) as Record<string, unknown>[];
    return {
        totalCount: (data.totalCount ?? data.TotalCount) as number,
        page: (data.page ?? data.Page) as number,
        pageSize: (data.pageSize ?? data.PageSize) as number,
        totalPages: (data.totalPages ?? data.TotalPages) as number,
        items: rawItems.map(toConfig),
    };
}

export const crosshairsService = {
    getAll: (search?: string, page = 1) => get("", search, page),
    getPro: (search?: string, page = 1) => get("/pro", search, page),
    getFun: (search?: string, page = 1) => get("/fun", search, page),
    /** Player-submitted, approved crosshairs only; ordered newest first. */
    getCommunity: (search?: string, page = 1, pageSize?: number) => get("/community", search, page, pageSize),
    /** Detail pages are addressed by slug; numeric ids only survive as legacy redirects. */
    getBySlug: async (slug: string): Promise<ICrosshairConfig> => {
        const response = await fetch(`/api/crosshairs/by-slug/${encodeURIComponent(slug)}`, {
            credentials: "include",
        });
        if (response.status === 404) throw new Error("NOT_FOUND");
        if (!response.ok) throw new Error(`Failed to fetch crosshair: ${response.status}`);
        return toDetailConfig(await response.json());
    },
    getById: async (id: number): Promise<ICrosshairConfig> => {
        const response = await fetch(`/api/crosshairs/${id}`, { credentials: "include" });
        if (response.status === 404) throw new Error("NOT_FOUND");
        if (!response.ok) throw new Error(`Failed to fetch crosshair: ${response.status}`);
        return toDetailConfig(await response.json());
    },
    /** Every approved crosshair in catalog order — the style/color pages filter it client-side. */
    getAllApproved: async (): Promise<ICrosshairConfig[]> => {
        const response = await fetch("/api/crosshairs/all", { credentials: "include" });
        if (!response.ok) throw new Error(`Failed to fetch crosshairs: ${response.status}`);
        return ((await response.json()) as Record<string, unknown>[]).map(toConfig);
    },
    /** Neighbouring approved crosshairs of the same kind, for the links under a detail page. */
    getRelated: async (id: number): Promise<IRelatedCrosshair[]> => {
        const response = await fetch(`/api/crosshairs/${id}/related`);
        if (!response.ok) return [];
        const rows = (await response.json()) as Record<string, unknown>[];
        return rows.map((r) => ({ id: r.id as number, name: r.name as string, slug: r.slug as string }));
    },
    /** Catalog crosshair with this exact code, or null if none (custom / unknown code). */
    findByCode: async (code: string): Promise<ICrosshairConfig | null> => {
        const url = new URL("/api/crosshairs/by-code", window.location.origin);
        url.searchParams.set("code", code);
        const response = await fetch(url.toString(), { credentials: "include" });
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Failed to resolve crosshair: ${response.status}`);
        const row = await response.json();
        return {
            id: row.id,
            name: row.name,
            slug: row.slug ?? String(row.id),
            code: row.code,
            isPro: row.isPro,
            isFun: row.isFun,
            favoriteCount: row.favoriteCount,
            isFavorited: row.isFavorited,
            submissionStatus: row.submissionStatus ?? null,
        };
    },
};
